import React, { useState } from "react";
import { IconButton } from "@fluentui/react/lib/Button";
import { Icon } from "@fluentui/react/lib/Icon";
import { ComponentSourceGroup } from "../../../api/models/component-source-group";
import { formatMessage, formatMessageWithParams } from "../../../utils/helpers/LanguageHelper";
import { generateIdentifier } from "../../../utils/helpers/RobotFramework";
import {
  CAPTION_CHANGE_COMPONENT_SOURCE_HIDE_INSTANCES,
  CAPTION_CHANGE_COMPONENT_SOURCE_MISSING_DOCUMENT,
  CAPTION_CHANGE_COMPONENT_SOURCE_SHOW_INSTANCES,
  CAPTION_COMPONENT_SOURCE_NEW_SOURCE,
  CAPTION_COMPONENT_SOURCE_UNDO_CHANGE,
} from "../outlineCaptions";
import { DEFAULT_FOLDER_CREATION } from "../WithApiFileLocationProvider";
import { PendingSourceChange } from "./usePendingSourceChanges";

const CAPTION_SEARCH = "cof.base.searchbar";

export interface SourceDocumentCardProps {
  /** The source-document group to render. */
  group: ComponentSourceGroup;
  /** The source document's containing folder path, without the library root. */
  path?: string;
  /** Content rendered inside the expanded region, typically the group's instance list. */
  children?: React.ReactNode;
  /** Called with this card's group when the search control is clicked. */
  onSearchClick?: (group: ComponentSourceGroup) => void;
  /** The staged replacement for this card's group, if any; overlays every displayed field. */
  pendingChange?: PendingSourceChange;
  /** Called with this card's group when the undo control is clicked. */
  onUndoChange?: (group: ComponentSourceGroup) => void;
}

/**
 * One source-document card: header with name, optional chain icon and search control, plus a
 * collapsible instances list.
 */
export function SourceDocumentCard({
  group,
  path,
  children,
  onSearchClick,
  pendingChange,
  onUndoChange,
}: SourceDocumentCardProps) {
  const [expanded, setExpanded] = useState(false);

  const isMultilinked = !pendingChange && group.resolution === "MULTILINKED_PART";
  const isMissing = !pendingChange && group.resolution === "UNAVAILABLE";
  const displayName = pendingChange
    ? (pendingChange.documentName ?? "").replace(/\.docx$/i, "")
    : isMissing
      ? formatMessage(CAPTION_CHANGE_COMPONENT_SOURCE_MISSING_DOCUMENT)
      : group.documentName.replace(/\.docx$/i, "");
  const rawPath = pendingChange ? pendingChange.path : path;
  const displayPath = typeof rawPath === "string" ? `${DEFAULT_FOLDER_CREATION}${rawPath}` : undefined;

  const toggleCaptionKey = expanded
    ? CAPTION_CHANGE_COMPONENT_SOURCE_HIDE_INSTANCES
    : CAPTION_CHANGE_COMPONENT_SOURCE_SHOW_INSTANCES;
  const toggleLabel = formatMessageWithParams(toggleCaptionKey, { count: group.instances.length });

  return (
    <div className="card component_source_card" data-testid="source-document-card">
      <div style={{ display: "flex", flex: 1, flexDirection: "column", minWidth: 0 }}>
        {/* Plain div, not a Fluent TextField: SourceDocumentCard.test.tsx expects no <input> in the card. */}
        <div
          data-testid="source-document-card-name"
          style={{
            alignItems: "center",
            background: "var(--color-white)",
            border: "1px solid var(--color-card-border)",
            borderRadius: 4,
            boxSizing: "border-box",
            display: "flex",
            gap: 6,
            minHeight: 32,
            minWidth: 0,
            overflow: "hidden",
            padding: "0 0 0 8px",
          }}
        >
          <span
            title={displayName}
            style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {displayName}
          </span>
          {isMultilinked && (
            <span
              aria-label={generateIdentifier(group.documentId, "link_icon")}
              className="k-icon k-i-link-vertical k-rotate-45 k-icon-sm"
            />
          )}
          {pendingChange && (
            <span
              data-testid="source-document-card-pending-marker"
              id={generateIdentifier(group.documentId, "new_source")}
              style={{ color: "var(--color-blue)", flexShrink: 0, fontSize: 12, fontStyle: "italic" }}
              title={formatMessage(CAPTION_COMPONENT_SOURCE_NEW_SOURCE)}
            >
              New!
            </span>
          )}
          <IconButton
            ariaLabel={formatMessage(CAPTION_SEARCH)}
            data-testid="source-document-card-search"
            iconProps={{ iconName: "Search" }}
            id={generateIdentifier(group.documentId, "search_button")}
            onClick={() => onSearchClick?.(group)}
            styles={{ root: { flexShrink: 0 } }}
          />
        </div>
        {!isMissing && displayPath !== undefined && (
          <div
            data-testid="source-document-card-path"
            title={displayPath}
            style={{
              alignItems: "center",
              color: "var(--color-grey)",
              display: "flex",
              fontSize: 12,
              gap: 4,
              marginTop: 4,
              minWidth: 0,
            }}
          >
            <span aria-hidden="true" className="k-icon k-i-folder k-icon-sm" style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{displayPath}</span>
          </div>
        )}
        {pendingChange && (
          <div style={{ alignItems: "center", display: "flex", gap: 10, marginTop: 4 }}>
            <button
              data-testid="source-document-card-undo"
              id={generateIdentifier(group.documentId, "undo_change")}
              onClick={() => onUndoChange?.(group)}
              style={{
                alignItems: "center",
                background: "none",
                border: "none",
                color: "var(--color-blue)",
                cursor: "pointer",
                display: "flex",
                fontSize: 12,
                gap: 4,
                padding: 0,
              }}
              type="button"
            >
              <Icon aria-hidden="true" iconName="Undo" style={{ fontSize: 12 }} />
              {formatMessage(CAPTION_COMPONENT_SOURCE_UNDO_CHANGE)}
            </button>
          </div>
        )}
      </div>
      {/* Native <button>: no custom onKeyDown needed, or Enter/Space would double-fire the click. */}
      <button
        aria-expanded={expanded}
        data-testid="source-document-card-toggle"
        id={generateIdentifier(group.documentId, "toggle_instances")}
        onClick={() => setExpanded((current) => !current)}
        style={{
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--color-blue)",
          cursor: "pointer",
          marginTop: 10,
          padding: 0,
        }}
        type="button"
      >
        {toggleLabel}
      </button>
      {expanded && children}
    </div>
  );
}
